<template>
    <div
        class="container-fluid w-100 h-100"
        style="overflow-x: hidden"
    >
        <p class="nopads mt-2">
            {{ $t("sensor_device.create_device") }}
        </p>
        <div class="d-flex justify-content-between align-items-center mb-2">
            <div class="d-flex w-100">
                <div class="w-100 me-3">
                    <span class="span-title">
                        {{ $t('sensor_device.name') }}
                    </span>
                    <input
                        id="device_name"
                        v-model="sensorDevice.name"
                        type="text"
                        class="form-control form-control-sm"
                        :class="{ 'is-invalid': validationErrors.name }"
                    >
                </div>
                <div class="w-100 me-3">
                    <span class="span-title">
                        {{ $t('sensor_device.device_id') }}
                    </span>
                    <input
                        id="device_id"
                        v-model="sensorDevice.device_id"
                        type="text"
                        class="form-control form-control-sm"
                        :class="{ 'is-invalid': validationErrors.device_id }"
                    >
                </div>
                <div class="w-100 me-3">
                    <span class="span-title">{{ $t('sensor_device.type') }}</span>
                    <select
                        v-model="sensorDevice.type"
                        class="form-select form-select-sm"
                        :class="{ 'is-invalid': validationErrors.type }"
                    >
                        <option
                            v-for="t in deviceTypes"
                            :key="t.value"
                            :value="t.value"
                        >
                            {{ t.label }}
                        </option>
                    </select>
                </div>
            </div>

            <button
                class="btn btn-primary form-button mt-auto mb-0"
                @click.prevent="createSensorDevice"
            >
                {{ $t("sensor_device.save") }}
            </button>
        </div>

        <p
            v-if="results.length === 0"
            class="span-title"
        >
            {{ $t("sensor_device.no_devices") }}
        </p>

        <SensorDeviceList
            v-if="results && results.length > 0"
            ref="sensorList"
            class="mt-4"
            :results="results"
            @open-modal="setSelectedDevice"
            @remove-sensor="removeSensor"
            @remove-device="removeDevice"
            @update-sensor="updateSensor"
            @edit-device="openDeviceEditModal"
            @update-sensors="updateSensors"
            @validation-error="showAlert"
        />

        <DeviceEditModal
            v-if="deviceEditModalOpen"
            :device="editDeviceData"
            :device-types="deviceTypes"
            @close="deviceEditModalOpen = false"
            @save="updateDevice"
        />

        <div
            v-if="loading"
            id="loader"
            class="spinner"
        />
    </div>
</template>

<script>
import { restApi } from '@/components/mixins/RestApiMixin';
import DeviceEditModal from './DeviceEditModal.vue';
import { inject } from 'vue';
import SensorDeviceList from './SensorDeviceList.vue';

export default {
    name: 'VehicleSensors',
    components: { SensorDeviceList, DeviceEditModal },
    mixins: [restApi],
    props: {
        vehicle: {
            type: Object,
            default: null
        },
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    data: function () {
        return {
            results: [],
            loading: false,
            editMode: false,
            selectedDevice: null,
            sensorDevice: {
                vehicle_id: this.vehicle.id,
                name: "",
                device_id: "",
                type: null
            },
            sensor: {
                name: "",
                unit: "",
                calibration_multiplier: 1
            },
            selectedSensor: {
                name: "",
                unit: "",
                calibration_multiplier: 1
            },
            deviceEditModalOpen: false,
            editDeviceData: null,
            sensorEditModalOpen: false,
            selectedSensorDeviceId: null,
            validationErrors: {
                name: false,
                device_id: false,
                type: false
            },

            sensorValidationErrors: {
                name: false,
                unit: false,
                calibration_multiplier: false
            },
            sensorEditValidationErrors: {
                name: false,
                unit: false,
                calibration_multiplier: false
            },
            deviceTypes: [
                { label: this.$t('sensor_device.type_paving'), value: 1 },
                { label: this.$t('sensor_device.type_positioning'), value: 2 },
                { label: this.$t('sensor_device.type_weather'), value: 3 },
                { label: this.$t('sensor_device.type_weighing'), value: 4 },
                { label: this.$t('sensor_device.type_other'), value: 5 },
            ]
        }
    },
    mounted() {
        this.sensorDevice.vehicle_id = this.vehicle.id
        this.fetchDevices()
    },
    methods: {
        fetchDevices() {
            this.restFetch(this.fetchSensorDeviceUrl(this.vehicle.id), this.fetchSuccess, this.fetchError)
        },
        createSensorDevice() {
            if (!this.validateSensorDevice()) {
                return;
            }
            this.restAdd(this.sensorDeviceUrl, this.sensorDevice, this.onSuccess, this.onError)
        },
        validateSensorDevice() {
            this.validationErrors = { name: false, device_id: false, type: false };
            let isValid = true;

            if (!this.sensorDevice.name.trim()) {
                this.validationErrors.name = true;
                isValid = false;
            }
            if (!this.sensorDevice.device_id.trim()) {
                this.validationErrors.device_id = true;
                isValid = false;
            }
            if (!this.sensorDevice.type) {
                this.validationErrors.type = true;
                isValid = false;
            }

            if (!isValid) {
                this.showAlert(this.$t("sensor_device.required_fields_missing"));
            }
            return isValid;
        },
        onSuccess() {
            this.validationErrors = { name: false, device_id: false, type: false };
            this.sensorDevice = {
                vehicle_id: this.vehicle.id,
                name: "",
                device_id: "",
                type: null
            };
            this.fetchDevices()
        },
        onError() {
            this.$t("sensor_device.device_create_error")
        },
        fetchError() {
            this.$t("sensor_device.fetch_device_error")
        },
        fetchSuccess(result) {
            this.results = result.data
        },
        setSelectedDevice(device) {
            this.editMode = true;
            this.selectedDevice = device
        },
        resetSensorObject() {
            this.sensor = {
                name: "",
                unit: "",
                calibration_multiplier: 1
            }
        },
        removeSensor(deviceId, sensor) {
            this.restDelete(this.removeSensorUrl(deviceId, sensor.id), this.onSensorRemove, this.onSensorRemoveError)
        },
        onSensorRemove() {
            this.fetchDevices()
        },
        onSensorRemoveError() {
            this.showAlert(this.$t("sensor_device.sensor_remove_error"))
        },
        removeDevice(deviceId) {
            this.restDelete(this.removeDeviceUrl(deviceId), this.deviceRemovedSuccesfully, this.onDeviceRemoveError);
        },
        deviceRemovedSuccesfully() {
            this.fetchDevices()
        },
        onDeviceRemoveError() {
            this.showAlert(this.$t("sensor_device.device_remove_error"))
        },
        sensorUpdateError() {
            this.showAlert(this.$t("sensor_device.sensor_update_error"))
        },
        updateSensor(deviceId, sensor) {
            if (String(sensor.id).startsWith('new_')) {
                this.restAdd(this.createSensorUrl(deviceId), sensor, this.sensorUpdateSuccess, this.sensorUpdateError)
            } else {
                this.restUpdate(this.updateSensorUrl(deviceId, sensor.id), sensor, () => this.sensorUpdateSuccess(sensor), this.sensorUpdateError)
            }
        },
        sensorUpdateSuccess(sensor) {
            if (sensor) {
                this.$refs.sensorList.originalSensors[sensor.id] = { ...sensor };
            } else {
                this.fetchDevices()
            }
        },
        updateSensors(deviceId, sensors) {
            this.restUpdate(this.updateSensorsUrl(deviceId), sensors, (data) => this.sensorsUpdatedSuccess(deviceId, data), () => this.showAlert(this.$t('sensor_device.sensor_update_error')))
        },
        sensorsUpdatedSuccess(deviceId, data) {
            const device = this.results.find(d => d.id === deviceId);

            if (device) {
                device.sensors = data;

                this.$refs.sensorList.updateOriginalSensors(data);

                this.showAlert(this.$t('sensor_device.sensors_updated'), 'success');
            }
        },
        openDeviceEditModal(device) {
            this.editDeviceData = { ...device }
            this.deviceEditModalOpen = true
        },
        updateDevice(deviceData) {
            this.restUpdate(
                `${this.sensorDeviceUrl}/${deviceData.id}`,
                { name: deviceData.name, device_id: deviceData.device_id, type: deviceData.type, is_active: deviceData.is_active },
                this.onDeviceUpdateSuccess,
                this.onDeviceUpdateError
            )
        },
        onDeviceUpdateSuccess() {
            this.deviceEditModalOpen = false
            this.fetchDevices()
        },
        onDeviceUpdateError() {
            this.showAlert(this.$t('sensor_device.device_update_error'))
        },
    }
}
</script>
